import React from 'react';
import propTypes from 'prop-types';
import Button from 'tailwind-components/Button/Button';
import { cn } from 'lib/utils';
import { isMobileWebView, postMobileOpenUrl } from 'helpers/mobileWebView';
import useAgentOwner from './useAgentOwner';

/**
 * consultant contact block shown in the sidebar and in the oneill home
 * @param className - extra classes for the card surface
 * @param title - heading above the contact details, empty hides the heading
 * @param subtitle - supporting line under the heading
 * @param showBookingButton - renders the zoho bookings call to action
 */
export default function AgentOwnerCard({
  className = '',
  title = 'Smooth Sailing Starts Here',
  subtitle = 'Real support. Real results.',
  showBookingButton = true
}) {
  const owner = useAgentOwner();

  if (!owner.isReady) return null;

  const handleOpenUrl = url => {
    if (isMobileWebView()) {
      postMobileOpenUrl(url);
      return;
    }
    window.open(url, '_blank');
  };

  return (
    <footer
      data-tour='agent-owner-card'
      className={cn('shrink-0 p-4', className)}
    >
      {title && (
        <div className='flex flex-col mb-2'>
          <h3 className='text-lg font-semibold text-left text-black/90 m-0'>
            {title}
          </h3>
          <h4 className='text-base font-normal text-left text-grey-light m-0'>
            {subtitle}
          </h4>
        </div>
      )}
      <div className='flex gap-3 justify-start items-center'>
        <img
          src={owner.imageSrc}
          alt={owner.displayName}
          className={cn(
            'w-[72px] h-[72px] rounded-full object-contain',
            owner.isTeamPhoto && 'object-cover'
          )}
        />
        <div className='flex flex-col justify-center leading-none flex-wrap'>
          <p className='text-sm font-medium mt-0 truncate leading-none'>
            {owner.displayName}
          </p>
          <p className='text-xs truncate mt-0 break-all leading-none'>
            {owner.displayEmail}
          </p>
          <p className='text-xs mt-0.5 mb-0 leading-none'>
            {owner.displayPhone}
          </p>
        </div>
      </div>
      {owner.bookingUrl && showBookingButton && (
        <div className='mt-2'>
          <Button
            fill
            text="Let's Connect"
            size='medium'
            variant='primaryLight'
            onClick={() => handleOpenUrl(owner.bookingUrl)}
          />
        </div>
      )}
    </footer>
  );
}

AgentOwnerCard.propTypes = {
  className: propTypes.string,
  title: propTypes.string,
  subtitle: propTypes.string,
  showBookingButton: propTypes.bool
};
